import { Clock3 } from 'lucide-react'

import {
  formatTimelineClock,
  minutesToPercent,
  TIMELINE_HOUR_MARKS,
  type WorkDayCategoryTimelineEntry,
  type WorkDayCategoryTimelineRow,
} from '@/lib/workday-category-timeline'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import {
  getWorkDayEntryStatusLabel,
  getWorkDayEntryStatusTimelineColor,
} from '@/lib/workday-entry-status'
import { cn } from '@/lib/utils'

type WorkdayCategoryWeekTimelineProps = {
  weekDays: Date[]
  rows: WorkDayCategoryTimelineRow[]
  mode?: 'week' | 'day'
  selectedDayKey?: string | null
  locale?: string
  serviceInstanceId?: string | null
  returnPath?: string | null
  isLoading?: boolean
  isRefreshing?: boolean
  selectedEntryId?: string | number | null
  onSelectEntry?: (entry: WorkDayCategoryTimelineEntry) => void
  onSelectDay?: (dayKey: string) => void
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function toIsoDateKey(value: Date) {
  const localDate = new Date(value)
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset())
  return localDate.toISOString().slice(0, 10)
}

export function WorkdayCategoryWeekTimeline({
  weekDays,
  rows,
  mode = 'week',
  selectedDayKey = null,
  locale = 'en-US',
  serviceInstanceId: _serviceInstanceId,
  returnPath: _returnPath,
  isLoading = false,
  isRefreshing = false,
  selectedEntryId,
  onSelectEntry,
  onSelectDay,
}: WorkdayCategoryWeekTimelineProps) {
  const refreshOverlay = isRefreshing ? (
    <div
      className="pointer-events-none absolute inset-0 z-10 rounded-2xl bg-background/20"
      aria-hidden="true"
    />
  ) : null

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border/70 bg-card/90 p-8 text-center text-sm text-muted-foreground">
        Loading weekly timeline…
      </div>
    )
  }

  if (rows.length === 0 && !isRefreshing) {
    return (
      <div className="rounded-2xl border border-dashed border-border/70 bg-card/60 p-10 text-center">
        <Clock3 className="mx-auto mb-3 h-8 w-8 text-muted-foreground/70" />
        <p className="text-sm font-medium text-foreground">No workday entries this week</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Scheduled workday entries with times will appear here, grouped by job category.
        </p>
      </div>
    )
  }

  const resolvedDayKey =
    mode === 'day'
      ? (() => {
        const fallbackDayKey = toIsoDateKey(weekDays[0] || new Date())
        if (!selectedDayKey) {
          return fallbackDayKey
        }
        const inVisibleWeek = weekDays.some((day) => toIsoDateKey(day) === selectedDayKey)
        return inVisibleWeek ? selectedDayKey : fallbackDayKey
      })()
      : null

  const resolvedDay =
    mode === 'day'
      ? weekDays.find((day) => toIsoDateKey(day) === resolvedDayKey) || weekDays[0] || new Date()
      : null

  if (mode === 'day' && resolvedDayKey && resolvedDay) {
    const dayRows = rows
      .map((row) => {
        const dayEntries = row.entriesByDate.get(resolvedDayKey) ?? []
        return {
          ...row,
          dayEntries,
        }
      })
      .filter((row) => row.dayEntries.length > 0)

    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border/70 bg-card/90 shadow-sm transition-opacity duration-150',
          isRefreshing && 'opacity-80',
        )}
      >
        {refreshOverlay}
        <div className="overflow-x-auto">
          <div className="min-w-[720px] p-4">
            <div className="mb-3 grid grid-cols-[minmax(180px,220px)_minmax(520px,1fr)] gap-2">
              <div />
              <button
                type="button"
                onClick={() => onSelectDay?.(resolvedDayKey)}
                className="rounded-lg border border-primary/40 bg-primary/5 px-2 py-2 text-center"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {resolvedDay.toLocaleDateString(locale, { weekday: 'short' })}
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {resolvedDay.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </button>
            </div>

            <div className="mb-2 grid grid-cols-[minmax(180px,220px)_minmax(520px,1fr)] gap-2">
              <div className="flex items-end px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Category
              </div>
              <div className="relative h-7">
                {TIMELINE_HOUR_MARKS.filter((hour) => hour % 2 === 0).map((hour) => (
                  <div
                    key={hour}
                    className="absolute top-0 -translate-x-1/2"
                    style={{ left: `${(hour / 24) * 100}%` }}
                  >
                    <span className="text-[9px] font-medium text-muted-foreground">{hour}</span>
                  </div>
                ))}
              </div>
            </div>

            {dayRows.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/70 bg-muted/15 p-6 text-center text-xs text-muted-foreground">
                {isRefreshing ? 'Loading day entries…' : 'No timed entries on this day.'}
              </div>
            ) : (
              <div className="space-y-2">
                {dayRows.map((row) => (
                  <div
                    key={`${row.categoryKey}-${resolvedDayKey}`}
                    className="grid grid-cols-[minmax(180px,220px)_minmax(520px,1fr)] items-stretch gap-2"
                  >
                    <div className="flex min-h-12 items-center gap-2 rounded-lg border border-border/60 bg-muted/10 px-3 py-2">
                      <span
                        className={cn('h-2.5 w-2.5 shrink-0 rounded-full bg-gradient-to-br', row.color.accent)}
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-xs font-semibold text-foreground">{row.categoryLabel}</p>
                        <p className="text-[10px] text-muted-foreground">{row.dayEntries.length} entries</p>
                      </div>
                    </div>

                    <div className="relative min-h-12 rounded-lg border border-border/60 bg-muted/20">
                      {TIMELINE_HOUR_MARKS.map((hour) => (
                        <div
                          key={`${row.categoryKey}-${resolvedDayKey}-${hour}`}
                          className="absolute inset-y-0 border-l border-border/35 first:border-l-0"
                          style={{ left: `${(hour / 24) * 100}%` }}
                        />
                      ))}

                      {row.dayEntries.map((timelineEntry) => {
                        const entry = timelineEntry.entry
                        const statusColor = getWorkDayEntryStatusTimelineColor(entry)
                        const statusLabel = entry.status_label || getWorkDayEntryStatusLabel(entry.status)
                        const leftPercent = minutesToPercent(timelineEntry.startMinutes)
                        const widthPercent = Math.max(
                          minutesToPercent(timelineEntry.endMinutes - timelineEntry.startMinutes),
                          2.5,
                        )
                        const jobLabel = formatJobTitleWithId({
                          jobId: entry.job,
                          title: entry.job_title,
                        })
                        const workerLabel = entry.worker_name?.trim() || 'Worker'
                        const timeLabel = `${formatTimelineClock(timelineEntry.startMinutes)} – ${formatTimelineClock(timelineEntry.endMinutes)}`
                        const isSelected = String(selectedEntryId ?? '') === String(entry.id)

                        return (
                          <button
                            key={String(entry.id)}
                            type="button"
                            onClick={() => onSelectEntry?.(timelineEntry)}
                            title={`${jobLabel} · ${workerLabel} · ${statusLabel} (${timeLabel})`}
                            className={cn(
                              'absolute top-1 bottom-1 overflow-hidden rounded-md border px-1.5 text-left shadow-sm transition hover:brightness-95',
                              statusColor.badge,
                              isSelected && 'ring-2 ring-primary/70 ring-offset-1 ring-offset-background',
                            )}
                            style={{
                              left: `${leftPercent}%`,
                              width: `${widthPercent}%`,
                            }}
                          >
                            <div
                              className={cn(
                                'absolute inset-y-0 left-0 w-1 bg-gradient-to-b',
                                statusColor.accent,
                              )}
                            />
                            <div className="relative pl-2">
                              <p className="line-clamp-1 text-[9px] font-semibold leading-tight">{workerLabel}</p>
                              <p className="line-clamp-1 text-[8px] opacity-80">{timeLabel}</p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-border/70 bg-card/90 shadow-sm transition-opacity duration-150',
        isRefreshing && 'opacity-80',
      )}
    >
      {refreshOverlay}
      <div className="overflow-x-auto">
        <div className="min-w-[960px] p-4">
          <div className="mb-3 grid grid-cols-[minmax(160px,200px)_repeat(7,minmax(110px,1fr))] gap-2">
            <div />
            {weekDays.map((day, index) => {
              const isToday = toIsoDateKey(day) === toIsoDateKey(new Date())
              return (
                <div
                  key={toIsoDateKey(day)}
                  className="rounded-lg"
                >
                  <button
                    type="button"
                    onClick={() => onSelectDay?.(toIsoDateKey(day))}
                    className={cn(
                      'w-full rounded-lg border px-2 py-2 text-center transition hover:border-primary/40 hover:bg-primary/5',
                      isToday ? 'border-primary/40 bg-primary/5' : 'border-border/60 bg-muted/15',
                    )}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {WEEKDAY_LABELS[index]}
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      {day.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
                    </p>
                  </button>
                </div>
              )
            })}
          </div>

          <div className="mb-2 grid grid-cols-[minmax(160px,200px)_repeat(7,minmax(110px,1fr))] gap-2">
            <div className="flex items-end px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Category
            </div>
            {weekDays.map((day) => (
              <div key={`scale-${toIsoDateKey(day)}`} className="relative h-7">
                {TIMELINE_HOUR_MARKS.filter((hour) => hour % 6 === 0).map((hour) => (
                  <div
                    key={hour}
                    className="absolute top-0 -translate-x-1/2"
                    style={{ left: `${(hour / 24) * 100}%` }}
                  >
                    <span className="text-[9px] font-medium text-muted-foreground">{hour}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="space-y-2">
            {rows.map((row) => (
              <div
                key={row.categoryKey}
                className="grid grid-cols-[minmax(160px,200px)_repeat(7,minmax(110px,1fr))] items-stretch gap-2"
              >
                <div className="flex min-h-12 items-center gap-2 rounded-lg border border-border/60 bg-muted/10 px-3 py-2">
                  <span
                    className={cn('h-2.5 w-2.5 shrink-0 rounded-full bg-gradient-to-br', row.color.accent)}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-xs font-semibold text-foreground">{row.categoryLabel}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {[...row.entriesByDate.values()].reduce((count, items) => count + items.length, 0)} entries
                    </p>
                  </div>
                </div>

                {weekDays.map((day) => {
                  const dateKey = toIsoDateKey(day)
                  const dayEntries = row.entriesByDate.get(dateKey) ?? []

                  return (
                    <div
                      key={`${row.categoryKey}-${dateKey}`}
                      className="relative min-h-12 rounded-lg border border-border/60 bg-muted/20"
                    >
                      {TIMELINE_HOUR_MARKS.map((hour) => (
                        <div
                          key={`${row.categoryKey}-${dateKey}-${hour}`}
                          className="absolute inset-y-0 border-l border-border/35 first:border-l-0"
                          style={{ left: `${(hour / 24) * 100}%` }}
                        />
                      ))}

                      {dayEntries.map((timelineEntry) => {
                        const entry = timelineEntry.entry
                        const statusColor = getWorkDayEntryStatusTimelineColor(entry)
                        const statusLabel = entry.status_label || getWorkDayEntryStatusLabel(entry.status)
                        const leftPercent = minutesToPercent(timelineEntry.startMinutes)
                        const widthPercent = Math.max(
                          minutesToPercent(timelineEntry.endMinutes - timelineEntry.startMinutes),
                          2.5,
                        )
                        const jobLabel = formatJobTitleWithId({
                          jobId: entry.job,
                          title: entry.job_title,
                        })
                        const workerLabel = entry.worker_name?.trim() || 'Worker'
                        const timeLabel = `${formatTimelineClock(timelineEntry.startMinutes)} – ${formatTimelineClock(timelineEntry.endMinutes)}`
                        const isSelected = String(selectedEntryId ?? '') === String(entry.id)

                        return (
                          <button
                            key={String(entry.id)}
                            type="button"
                            onClick={() => onSelectEntry?.(timelineEntry)}
                            title={`${jobLabel} · ${workerLabel} · ${statusLabel} (${timeLabel})`}
                            className={cn(
                              'absolute top-1 bottom-1 overflow-hidden rounded-md border px-1.5 text-left shadow-sm transition hover:brightness-95',
                              statusColor.badge,
                              isSelected && 'ring-2 ring-primary/70 ring-offset-1 ring-offset-background',
                            )}
                            style={{
                              left: `${leftPercent}%`,
                              width: `${widthPercent}%`,
                            }}
                          >
                            <div
                              className={cn(
                                'absolute inset-y-0 left-0 w-1 bg-gradient-to-b',
                                statusColor.accent,
                              )}
                            />
                            <div className="relative pl-2">
                              <p className="line-clamp-1 text-[9px] font-semibold leading-tight">{workerLabel}</p>
                              <p className="line-clamp-1 text-[8px] opacity-80">{timeLabel}</p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
